import React from 'react';
import { Icon } from '../Icon';
import { IconColor } from '../Icon/icon-color.enum';
import { IconMotif } from '../Icon/icon-motif.enum';
import { IconVariant } from '../Icon/icon-variant.enum';

/**
 * Fact
 * Displays an icon alongside text or HTML content with proper alignment and spacing.
 * This component is used to present factual information with a visual icon indicator.
 *
 * Behavior
 * - Renders an Icon component with the specified motif, color, and variant.
 * - Displays the children content (text or HTML) next to the icon with consistent spacing.
 * - Uses flexbox layout to ensure proper alignment between icon and content.
 * - Applies a margin-end (me-2) to the icon for consistent spacing.
 *
 * Props
 * - children: The content to display next to the icon (can be text, HTML, or React components).
 * - iconMotif: The icon motif/symbol to display (required).
 * - iconColor?: Optional color for the icon.
 * - iconVariant?: Optional variant style for the icon.
 * - altText?: Optional alternative text for accessibility.
 * - className?: Optional additional CSS classes for the wrapper div.
 *
 * Example
 * <Fact
 *   iconMotif={IconMotif.Info}
 *   iconColor={IconColor.Primary}
 *   iconVariant={IconVariant.Filled}
 *   altText="Information"
 *   className="mb-3"
 * >
 *   This is important information about the service.
 * </Fact>
 */
export default function Fact({
  children,
  iconMotif,
  iconColor,
  iconVariant,
  altText,
  className = '',
}: {
  children: React.ReactNode;
  iconMotif: IconMotif;
  iconColor?: IconColor;
  iconVariant?: IconVariant;
  altText?: string | undefined;
  className?: string;
}) {
  return (
    <div className={`d-flex align-items-center ${className}`}>
      <Icon motif={iconMotif} color={iconColor} variant={iconVariant} altText={altText} className="me-2" />
      {children}
    </div>
  );
}
